import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";

const API_URL = process.env.REACT_APP_URL || "";

/* ═══════════════ ANIMATIONS ═══════════════ */
const float1 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(60px,-80px) scale(1.1)}`;
const float2 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-80px,60px) scale(1.15)}`;
const float3 = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(40px,70px) scale(0.95)}`;
const spinSlow = keyframes`0%{transform:rotate(0)}100%{transform:rotate(360deg)}`;

const SpinnerAnim = createGlobalStyle`
  @keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
  .rpk-input:focus { border-color: #BED701 !important; box-shadow: 0 0 12px rgba(190,215,1,0.15); background: rgba(190,215,1,0.06) !important; }
`;

/* ═══════════════ STYLED ═══════════════ */
const Page = styled.div`
  min-height: calc(100dvh - 64px);
  width: 100vw;
  max-width: 100%;
  box-sizing: border-box;
  background: #f8f9fa;
  position: relative;
  overflow: clip;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const GridOverlay = styled.div`
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(190,215,1,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(190,215,1,0.06) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, black 20%, transparent 100%);
`;

const Blob = styled.div`
  position: absolute; border-radius: 50%; filter: blur(120px); opacity: 0.25;
  ${p => p.$color === "primary" && css`background: #BED701; width: 400px; height: 400px; top: -100px; left: -100px; animation: ${float1} 20s ease-in-out infinite;`}
  ${p => p.$color === "secondary" && css`background: #ceea02; width: 300px; height: 300px; bottom: -50px; right: -80px; animation: ${float2} 25s ease-in-out infinite;`}
  ${p => p.$color === "accent" && css`background: #8a9e01; width: 200px; height: 200px; top: 50%; left: 60%; animation: ${float3} 18s ease-in-out infinite;`}
`;

const GlassCard = styled(motion.div)`
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  border: 1px solid rgba(255,255,255,0.8);
  border-radius: 28px;
  padding: ${p => p.$large ? "48px 40px" : "32px"};
  width: 100%;
  box-sizing: border-box;
  max-width: ${p => p.$wide ? "520px" : "420px"};
  position: relative;
  z-index: 1;
  box-shadow: 0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04);

  &::before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: 29px;
    background: linear-gradient(135deg, rgba(190,215,1,0.3) 0%, transparent 50%, rgba(206,234,2,0.2) 100%);
    z-index: -1;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    padding: 1px;
  }

  @media (max-width: 500px) {
    padding: ${p => p.$large ? "32px 20px" : "24px 16px"};
  }
`;

const GradientBtn = styled.button`
  width: 100%; height: 52px;
  background: linear-gradient(135deg, #ceea02 0%, #BED701 50%, #a2b702 100%);
  color: #0a0a0a; font-weight: 700; font-size: 15px;
  border: none; border-radius: 14px; cursor: pointer;
  transition: all 0.25s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  position: relative; overflow: hidden;

  &::after {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
    transform: translateX(-100%);
    transition: transform 0.5s;
  }
  &:hover:not(:disabled)::after { transform: translateX(100%); }
  &:hover:not(:disabled) { box-shadow: 0 0 30px rgba(190,215,1,0.4), 0 0 60px rgba(190,215,1,0.15); transform: translateY(-1px); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.25; cursor: not-allowed; }
`;

const Badge = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 18px;
  background: rgba(190,215,1,0.1);
  border: 1px solid rgba(190,215,1,0.2);
  border-radius: 100px;
  font-size: 14px; color: #555;
`;

const Spinner = () => <div style={{ width: 18, height: 18, border: "2px solid rgba(0,0,0,0.15)", borderTop: "2px solid #0a0a0a", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />;

/* ═══════════════ FULLSCREEN SIGNATURE ═══════════════ */
const FullscreenSignature = ({ onConfirm, onClose }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const initCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    const rect = c.parentElement.getBoundingClientRect();
    c.width = rect.width; c.height = rect.height;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#1a1a2e"; ctx.lineWidth = 4; ctx.lineCap = "round"; ctx.lineJoin = "round";
  };

  const clear = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); setHasDrawn(false); initCanvas(); };

  useEffect(() => {
    initCanvas();
    const c = canvasRef.current; if (!c) return;

    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const down = (e) => { e.preventDefault(); const p = pos(e); const ctx = c.getContext("2d"); ctx.beginPath(); ctx.moveTo(p.x, p.y); drawing.current = true; };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); const ctx = c.getContext("2d"); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const up = () => { drawing.current = false; };

    const opts = { passive: false };
    c.addEventListener("touchstart", down, opts);
    c.addEventListener("touchmove", move, opts);
    c.addEventListener("touchend", up);
    c.addEventListener("mousedown", down);
    c.addEventListener("mousemove", move);
    c.addEventListener("mouseup", up);
    c.addEventListener("mouseleave", up);

    const h = () => setTimeout(initCanvas, 100);
    window.addEventListener("resize", h);
    window.addEventListener("orientationchange", h);

    return () => {
      c.removeEventListener("touchstart", down, opts);
      c.removeEventListener("touchmove", move, opts);
      c.removeEventListener("touchend", up);
      c.removeEventListener("mousedown", down);
      c.removeEventListener("mousemove", move);
      c.removeEventListener("mouseup", up);
      c.removeEventListener("mouseleave", up);
      window.removeEventListener("resize", h);
      window.removeEventListener("orientationchange", h);
    };
  }, []);

  const confirm = () => {
    if (!hasDrawn) return;
    const src = canvasRef.current;
    const ctx = src.getContext("2d");
    const imgData = ctx.getImageData(0, 0, src.width, src.height);
    const { data, width, height } = imgData;

    // Find bounding box of drawn content
    let top = height, left = width, bottom = 0, right = 0;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const a = data[(y * width + x) * 4 + 3];
        if (a > 0) { if (y < top) top = y; if (y > bottom) bottom = y; if (x < left) left = x; if (x > right) right = x; }
      }
    }
    if (bottom <= top || right <= left) {
      onConfirm(src.toDataURL("image/png")); return;
    }

    // Add padding
    const pad = 10;
    top = Math.max(0, top - pad); left = Math.max(0, left - pad);
    bottom = Math.min(height - 1, bottom + pad); right = Math.min(width - 1, right + pad);

    const trimW = right - left + 1;
    const trimH = bottom - top + 1;

    // Scale to match Apply System SignaturePadModal output size (~480px max width)
    const maxW = 480;
    const sc = trimW > maxW ? maxW / trimW : 1;
    const outW = Math.max(Math.round(trimW * sc), 188);
    const outH = Math.max(Math.round(trimH * sc), 60);

    // First: draw scaled signature on temp canvas
    const tmp = document.createElement("canvas");
    tmp.width = outW; tmp.height = outH;
    const tmpCtx = tmp.getContext("2d");
    tmpCtx.drawImage(src, left, top, trimW, trimH, 0, 0, outW, outH);

    // Second: build output with white bg and thickened lines via 1px offset copies
    const out = document.createElement("canvas");
    out.width = outW + 2; out.height = outH + 2;
    const outCtx = out.getContext("2d");
    outCtx.fillStyle = "#ffffff";
    outCtx.fillRect(0, 0, out.width, out.height);
    for (let dx = 0; dx <= 2; dx++) {
      for (let dy = 0; dy <= 2; dy++) {
        outCtx.drawImage(tmp, dx, dy);
      }
    }

    onConfirm(out.toDataURL("image/png"));
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#f8f9fa", display: "flex", flexDirection: "column" }}>
      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "white", borderBottom: "1px solid #e8e8e8", flexShrink: 0 }}>
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", fontSize: 14, fontWeight: 600, color: "#666", background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 10, cursor: "pointer" }}>
          ← Zpět
        </button>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#999" }}>Podpis účastníka</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={clear} style={{ padding: "8px 16px", fontSize: 14, fontWeight: 600, color: "#888", background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 10, cursor: "pointer" }}>
            Vymazat
          </button>
          <button onClick={confirm} disabled={!hasDrawn}
            style={{ padding: "8px 20px", fontSize: 14, fontWeight: 700, color: "#1a1a1a", background: hasDrawn ? "linear-gradient(135deg, #ceea02, #BED701)" : "#e8e8e8", border: "none", borderRadius: 10, cursor: hasDrawn ? "pointer" : "not-allowed", opacity: hasDrawn ? 1 : 0.4, boxShadow: hasDrawn ? "0 4px 12px rgba(190,215,1,0.3)" : "none" }}>
            Potvrdit ✓
          </button>
        </div>
      </div>

      {/* Canvas area — fills all remaining space */}
      <div style={{ flex: 1, position: "relative", margin: 16, borderRadius: 20, overflow: "hidden", background: "white", border: "2px solid #e8e8e8", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}>
        <canvas ref={canvasRef}
          style={{ width: "100%", height: "100%", cursor: "crosshair", touchAction: "none", display: "block" }} />
        {!hasDrawn && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none", color: "#d0d0d0", gap: 8 }}>
            <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" /></svg>
            <span style={{ fontSize: 16, fontWeight: 500 }}>Podepište se zde</span>
            <span style={{ fontSize: 13 }}>Prstem nebo stylusem</span>
          </div>
        )}
        {/* Signature line */}
        <div style={{ position: "absolute", bottom: 40, left: 40, right: 40, height: 1, background: "#e0e0e0" }} />
        <span style={{ position: "absolute", bottom: 20, left: 40, fontSize: 11, color: "#ccc" }}>× podpis účastníka</span>
      </div>
    </div>
  );
};

/* ═══════════════ SIGNATURE PAD (INLINE + FULLSCREEN) ═══════════════ */
const SignaturePad = ({ onSave, onSignedChange }) => {
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [showFullscreen, setShowFullscreen] = useState(false);

  useEffect(() => { if (onSave) onSave(() => signatureDataUrl); }, [signatureDataUrl, onSave]);
  useEffect(() => { if (onSignedChange) onSignedChange(!!signatureDataUrl); }, [signatureDataUrl, onSignedChange]);

  return (
    <div>
      {/* Inline preview / trigger */}
      <div
        onClick={() => setShowFullscreen(true)}
        style={{ position: "relative", borderRadius: 14, overflow: "hidden", border: signatureDataUrl ? "2px solid #BED701" : "2px solid #e0e0e0", background: signatureDataUrl ? "#ffffff" : "rgba(255,255,255,0.6)", cursor: "pointer", minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.25s", boxSizing: "border-box" }}
      >
        {signatureDataUrl ? (
          <img src={signatureDataUrl} alt="Podpis" style={{ maxHeight: 90, maxWidth: "80%", objectFit: "contain", padding: 8 }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, color: "#bbb", padding: "20px 0" }}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" /></svg>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Klepněte pro podpis</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {signatureDataUrl && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={() => setSignatureDataUrl("")}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#888", background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 10, cursor: "pointer" }}>
              ✕ Vymazat
            </button>
            <button type="button" onClick={() => setShowFullscreen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#666", background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 10, cursor: "pointer" }}>
              ✎ Přepsat
            </button>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#BED701" }}>✓ Podepsáno</span>
        </div>
      )}

      {/* Fullscreen modal — portal to body */}
      {showFullscreen && createPortal(
        <FullscreenSignature
          onConfirm={(dataUrl) => { setSignatureDataUrl(dataUrl); setShowFullscreen(false); }}
          onClose={() => setShowFullscreen(false)}
        />,
        document.body
      )}
    </div>
  );
};

/* ═══════════════ PIN SCREEN ═══════════════ */
const PinScreen = ({ onVerified }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const refs = useRef([]);

  const set = (i, v) => { if (!/^\d?$/.test(v)) return; const a = pin.split(""); a[i] = v; setPin(a.join("").slice(0, 6)); if (v && i < 5) refs.current[i + 1]?.focus(); };
  const keyDown = (i, e) => { if (e.key === "Backspace" && !pin[i] && i > 0) refs.current[i - 1]?.focus(); };
  const paste = (e) => { const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6); if (p) setPin(p); e.preventDefault(); };

  const submit = async (e) => {
    e.preventDefault(); if (pin.length !== 6) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_URL}/training/verify-pin`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pin }) });
      if (res.ok) onVerified(pin, await res.json());
      else {
        let msg = "Neplatný nebo expirovaný kód";
        try { const d = await res.json(); if (d.message) msg = d.message; } catch {}
        setError(msg);
      }
    } catch { setError("Nepodařilo se ověřit kód, zkuste to znovu"); }
    setLoading(false);
  };

  return (
    <Page>
      <SpinnerAnim />
      <GridOverlay />
      <Blob $color="primary" /><Blob $color="secondary" /><Blob $color="accent" />

      <GlassCard initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} $large>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <motion.div initial={{ scale: 0, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", delay: 0.15 }}
            style={{ width: 68, height: 68, background: "linear-gradient(135deg, #ceea02, #BED701)", borderRadius: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20, boxShadow: "0 0 40px rgba(190,215,1,0.3)" }}>
            <svg width="34" height="34" fill="none" stroke="white" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
            </svg>
          </motion.div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", margin: 0, letterSpacing: "-0.5px" }}>Školení VZV</h1>
          <p style={{ fontSize: 14, color: "#999", marginTop: 8 }}>Zadejte přístupový kód od školitele</p>
        </div>

        <form onSubmit={submit}>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 28 }}>
            {[0,1,2,3,4,5].map((i) => (
              <motion.input key={i} ref={(el) => refs.current[i] = el}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.06 }}
                type="text" inputMode="numeric" maxLength={1} value={pin[i] || ""}
                onChange={(e) => set(i, e.target.value)} onKeyDown={(e) => keyDown(i, e)} onPaste={i === 0 ? paste : undefined}
                autoFocus={i === 0}
                style={{
                  width: 50, height: 60, textAlign: "center", fontSize: 24, fontWeight: 700, borderRadius: 14, outline: "none",
                  border: pin[i] ? "2px solid #BED701" : "2px solid #e0e0e0",
                  background: pin[i] ? "rgba(190,215,1,0.06)" : "white",
                  color: "#1a1a1a", boxShadow: pin[i] ? "0 0 20px rgba(190,215,1,0.12)" : "none", transition: "all 0.25s",
                }} />
            ))}
          </div>
          {error && <p style={{ color: "#f87171", fontSize: 13, textAlign: "center", marginBottom: 16 }}>{error}</p>}
          <GradientBtn type="submit" disabled={pin.length !== 6 || loading}>
            {loading ? <><Spinner /> Ověřuji...</> : "Pokračovat →"}
          </GradientBtn>
        </form>
        <p style={{ textAlign: "center", fontSize: 11, color: "#ccc", marginTop: 28, letterSpacing: 1.5 }}>RPK TECHNIK s.r.o.</p>
      </GlassCard>
    </Page>
  );
};

/* ═══════════════ PARTICIPANT FORM ═══════════════ */
const ParticipantForm = ({ pin, sessionInfo, onParticipantAdded, participantCount }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [hasSigned, setHasSigned] = useState(false);
  const sigRef = useRef(null);
  const dobMonthRef = useRef(null);
  const dobYearRef = useRef(null);

  const buildDateOfBirth = () => {
    if (!dobDay || !dobMonth || !dobYear || dobYear.length < 4) return "";
    const day = parseInt(dobDay); const month = parseInt(dobMonth); const year = parseInt(dobYear);
    const now = new Date().getFullYear();
    if (year < now - 90 || year > now) return "";
    if (month < 1 || month > 12) return "";
    if (day < 1 || day > 31) return "";
    return `${dobYear}-${dobMonth.padStart(2, "0")}-${dobDay.padStart(2, "0")}`;
  };

  const getDateError = () => {
    if (!dobDay && !dobMonth && !dobYear) return "";
    if (dobYear.length === 4) {
      const year = parseInt(dobYear); const now = new Date().getFullYear();
      if (year < now - 90 || year > now) return "Neplatný rok";
    }
    if (dobMonth && (parseInt(dobMonth) < 1 || parseInt(dobMonth) > 12)) return "Neplatný měsíc";
    if (dobDay && (parseInt(dobDay) < 1 || parseInt(dobDay) > 31)) return "Neplatný den";
    return "";
  };

  const handleDobDay = (v) => {
    const num = v.replace(/\D/g, "").slice(0, 2);
    setDobDay(num);
    if (num.length === 2 || (num.length === 1 && parseInt(num) > 3)) dobMonthRef.current?.focus();
  };
  const handleDobMonth = (v) => {
    const num = v.replace(/\D/g, "").slice(0, 2);
    setDobMonth(num);
    if (num.length === 2 || (num.length === 1 && parseInt(num) > 1)) dobYearRef.current?.focus();
  };
  const handleDobYear = (v) => {
    setDobYear(v.replace(/\D/g, "").slice(0, 4));
  };

  const submit = async (e) => {
    e.preventDefault(); if (!firstName.trim() || !lastName.trim() || !buildDateOfBirth() || !hasSigned) return;
    setSaving(true); setSubmitError("");
    try {
      const sig = sigRef.current ? sigRef.current() : "";
      const dateOfBirth = buildDateOfBirth();
      const res = await fetch(`${API_URL}/training/add-participant`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, firstName: firstName.trim(), lastName: lastName.trim(), dateOfBirth, signatureBase64: sig }),
      });
      if (res.ok) { const d = await res.json(); setShowSuccess(true); setTimeout(() => { setFirstName(""); setLastName(""); setDobDay(""); setDobMonth(""); setDobYear(""); setHasSigned(false); setSubmitError(""); setShowSuccess(false); onParticipantAdded(d.participantCount); }, 2200); }
      else { let msg = "Nepodařilo se uložit data, zkuste to znovu"; try { const d = await res.json(); if (d.message) msg = d.message; } catch {} setSubmitError(msg); }
    } catch (err) { console.error("AddParticipant error:", err); setSubmitError("Chyba připojení, zkuste to znovu"); }
    setSaving(false);
  };

  return (
    <Page style={{ justifyContent: "flex-start", paddingTop: 40 }}>
      <SpinnerAnim />
      <GridOverlay />
      <Blob $color="primary" /><Blob $color="secondary" /><Blob $color="accent" />

      <div style={{ width: "100%", maxWidth: 520, position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", margin: 0 }}>Vyplňte své údaje</h1>
          <p style={{ fontSize: 13, color: "#aaa", marginTop: 6 }}>Školitel: <span style={{ color: "#555" }}>{sessionInfo?.trainerName || "—"}</span></p>
          <div style={{ marginTop: 14 }}>
            <Badge>
              <svg width="16" height="16" fill="none" stroke="#BED701" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
              <strong style={{ color: "#BED701" }}>{participantCount}</strong>
              <span>{participantCount === 1 ? "účastník" : participantCount < 5 ? "účastníci" : "účastníků"}</span>
            </Badge>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {showSuccess ? (
            <GlassCard key="ok" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} $wide
              style={{ textAlign: "center", padding: "60px 32px" }}>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12 }}
                style={{ width: 80, height: 80, background: "rgba(190,215,1,0.1)", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20, border: "2px solid rgba(190,215,1,0.2)", boxShadow: "0 0 40px rgba(190,215,1,0.15)" }}>
                <svg width="40" height="40" fill="none" stroke="#BED701" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </motion.div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", margin: 0 }}>Uloženo!</h2>
              <p style={{ fontSize: 14, color: "#999", marginTop: 8 }}>Předejte tablet dalšímu účastníkovi</p>
            </GlassCard>
          ) : (
            <GlassCard key="f" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} $wide $large>
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#bbb", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1.5 }}>Jméno *</p>
                  <input
                    className="rpk-input"
                    type="text" placeholder="Zadejte jméno" value={firstName}
                    onChange={(e) => setFirstName(e.target.value)} autoFocus autoComplete="off"
                    style={{ width: "100%", height: 48, padding: "0 16px", fontSize: 16, fontWeight: 500, borderRadius: 14, border: firstName ? "2px solid #BED701" : "2px solid #e0e0e0", background: firstName ? "rgba(190,215,1,0.06)" : "rgba(255,255,255,0.6)", color: "#1a1a1a", outline: "none", transition: "all 0.25s", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#bbb", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1.5 }}>Příjmení *</p>
                  <input
                    className="rpk-input"
                    type="text" placeholder="Zadejte příjmení" value={lastName}
                    onChange={(e) => setLastName(e.target.value)} autoComplete="off"
                    style={{ width: "100%", height: 48, padding: "0 16px", fontSize: 16, fontWeight: 500, borderRadius: 14, border: lastName ? "2px solid #BED701" : "2px solid #e0e0e0", background: lastName ? "rgba(190,215,1,0.06)" : "rgba(255,255,255,0.6)", color: "#1a1a1a", outline: "none", transition: "all 0.25s", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#bbb", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1.5 }}>Datum narození</p>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {[
                      { ref: undefined, placeholder: "DD", value: dobDay, onChange: handleDobDay, maxLength: 2, filled: !!dobDay },
                      { ref: dobMonthRef, placeholder: "MM", value: dobMonth, onChange: handleDobMonth, maxLength: 2, filled: !!dobMonth },
                      { ref: dobYearRef, placeholder: "RRRR", value: dobYear, onChange: handleDobYear, maxLength: 4, filled: dobYear.length === 4 },
                    ].map((f, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span style={{ fontSize: 20, fontWeight: 300, color: "#ccc", flexShrink: 0 }}>.</span>}
                        <input
                          ref={f.ref}
                          className="rpk-input"
                          type="text" inputMode="numeric" placeholder={f.placeholder} maxLength={f.maxLength} value={f.value}
                          onChange={(e) => f.onChange(e.target.value)} autoComplete="off"
                          style={{ minWidth: 0, flex: f.maxLength === 4 ? 2 : 1, height: 48, textAlign: "center", fontSize: 18, fontWeight: 600, borderRadius: 14, border: f.filled ? "2px solid #BED701" : "2px solid #e0e0e0", background: f.filled ? "rgba(190,215,1,0.06)" : "rgba(255,255,255,0.6)", color: "#1a1a1a", outline: "none", transition: "all 0.25s", boxSizing: "border-box" }}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                  {getDateError() && <p style={{ color: "#f87171", fontSize: 12, marginTop: 8 }}>{getDateError()}</p>}
                </div>

                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#bbb", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1.5 }}>Podpis</p>
                  <SignaturePad onSave={(fn) => { sigRef.current = fn; }} onSignedChange={setHasSigned} />
                </div>

                {submitError && <p style={{ color: "#f87171", fontSize: 13, textAlign: "center" }}>{submitError}</p>}
                <GradientBtn type="submit" disabled={!firstName.trim() || !lastName.trim() || !buildDateOfBirth() || !hasSigned || saving} style={{ marginTop: 4 }}>
                  {saving ? <><Spinner /> Ukládám...</> : "Potvrdit a předat dalšímu →"}
                </GradientBtn>
              </form>
            </GlassCard>
          )}
        </AnimatePresence>
      </div>
    </Page>
  );
};

/* ═══════════════ MAIN ═══════════════ */
const Training = () => {
  const [verified, setVerified] = useState(() => !!sessionStorage.getItem("trainingPin"));
  const [pin, setPin] = useState(() => sessionStorage.getItem("trainingPin") || "");
  const [sessionInfo, setSessionInfo] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("trainingSessionInfo")); } catch { return null; }
  });
  const [participantCount, setParticipantCount] = useState(() => parseInt(sessionStorage.getItem("trainingCount") || "0"));

  // Clean up session data when navigating away from /skoleni
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("trainingPin");
      sessionStorage.removeItem("trainingSessionInfo");
      sessionStorage.removeItem("trainingCount");
    };
  }, []);

  const handleVerified = (p, d) => {
    setPin(p); setSessionInfo(d); setParticipantCount(d.participantCount || 0); setVerified(true);
    sessionStorage.setItem("trainingPin", p);
    sessionStorage.setItem("trainingSessionInfo", JSON.stringify(d));
    sessionStorage.setItem("trainingCount", String(d.participantCount || 0));
  };

  const handleParticipantAdded = (count) => {
    setParticipantCount(count);
    sessionStorage.setItem("trainingCount", String(count));
  };

  return verified ? (
    <ParticipantForm pin={pin} sessionInfo={sessionInfo} onParticipantAdded={handleParticipantAdded} participantCount={participantCount} />
  ) : (
    <PinScreen onVerified={handleVerified} />
  );
};

export default Training;
